import { TaskTypeTransformMap } from '@api/models/task';
import type { ColumnDef } from '@tanstack/vue-table';
import * as changeCase from 'change-case';
import { h } from 'vue';
import { RouterLink } from 'vue-router';

import type { Partner, Task, TaskCollection } from '@stuut/api/models';
import { TaskStatus } from '@stuut/api/models';
import { humanize } from '@stuut/common/src/utils';
import { Checkbox } from '@ui/components/checkbox';
import { DataTableColumnHeader } from '@ui/components/datatable';
import { Timestamp } from '@ui/components/time';
import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/tooltip';

import TaskPriority from '@/components/common/TaskPriority.vue';
import { PartnerQuickView } from '@/components/partners';
import TasksAssigneeSelect from '@/components/tasks/TasksAssigneeSelect.vue';
import TaskStatusBadge from '@/components/tasks/TaskStatusBadge.vue';
import { useTaskCollectionStore, useTaskStore } from '@/store';

let _taskCollectionStore: ReturnType<typeof useTaskCollectionStore> | null = null;

let _taskStore: ReturnType<typeof useTaskStore> | null = null;

const getTaskStore = () => {
  if (!_taskStore) {
    _taskStore = useTaskStore();
  }
  return _taskStore;
};

const getTaskCollectionStore = () => {
  if (!_taskCollectionStore) {
    _taskCollectionStore = useTaskCollectionStore();
  }
  return _taskCollectionStore;
};

export const TaskTableColumns: ColumnDef<Task>[] = [
  {
    cell: ({ row }) => {
      return h(
        'div',
        { class: 'flex flex-row justify-center' },
        h(Tooltip, [
          h(
            TooltipTrigger,
            h(Checkbox, {
              ariaLabel: 'Select row',
              disabled: row.original.status !== TaskStatus.OPEN,
              modelValue: row.getIsSelected(),
              'onUpdate:modelValue': (value: any) => row.toggleSelected(Boolean(value)),
            }),
          ),
          row.original.status !== TaskStatus.OPEN &&
            h(
              TooltipContent,
              { class: 'max-w-[250px]', side: 'right' },
              'Only tasks in an Open status can be selected to perform actions on.',
            ),
        ]),
      );
    },
    enableHiding: false,
    enableSorting: false,
    header: ({ table }) => {
      const selectableRows = table
        .getFilteredRowModel()
        .rows.filter((row) => row.original.status === TaskStatus.OPEN);
      const allSelectableSelected =
        selectableRows.length > 0 && selectableRows.every((row) => row.getIsSelected());
      return h(
        'div',
        { class: 'flex flex-row justify-center' },
        h(Checkbox, {
          ariaLabel: 'Select all',
          indeterminate: table.getIsSomePageRowsSelected(),
          modelValue: allSelectableSelected,
          'onUpdate:modelValue': (value: any) =>
            selectableRows.forEach((row) => row.toggleSelected(Boolean(value))),
        }),
      );
    },
    id: 'select',
    size: 36,
  },
  {
    accessorKey: 'partner.computedName',
    cell: ({ row }) =>
      h(
        'div',
        { class: 'w-fit' },
        h(PartnerQuickView, {
          partner: row.original.partner as Partner,
        }),
      ),
    enableHiding: false,
    enableSorting: false,
    header: ({ column }) => h(DataTableColumnHeader, { column, title: 'Customer' }),
    size: 130,
  },
  {
    accessorKey: 'task_type',
    cell: ({ row }) => {
      return h('div', { class: 'flex items-center gap-x-1.5 text-xs truncate' }, [
        h('i', {
          class: ['text-center w-6', row.original.taskTypeIcon, row.original.taskTypeIconClass],
        }),
        h('span', row.original?.taskType),
      ]);
    },
    enableHiding: false,
    enableSorting: false,
    header: ({ column }) => h(DataTableColumnHeader, { column, title: 'Type' }),
    meta: {
      class: 'truncate',
    },
    size: 140,
  },
  {
    accessorKey: 'classifications',
    cell: ({ row }) => {
      const customDescription = h(
        'div',
        { class: 'flex items-center gap-x-1.5 text-2xs truncate' },
        [
          h('i', { class: row.original.customDescription?.icon }),
          h('span', { class: 'truncate' }, row.original.customDescription?.label),
        ],
      );

      return row.original?.customDescription?.label
        ? customDescription
        : row.original?.classifications?.length
          ? h(Tooltip, [
              h(
                TooltipTrigger,
                { asChild: true },
                h(
                  'span',
                  { class: 'truncate text-2xs' },
                  row.original?.classifications?.map((c) => humanize(c))?.join(', '),
                ),
              ),
              h(
                TooltipContent,
                { side: 'left' },
                row.original?.classifications?.map((c) => humanize(c))?.join(', '),
              ),
            ])
          : '--';
    },
    enableHiding: false,
    enableSorting: false,
    header: ({ column }) => h(DataTableColumnHeader, { column, title: '' }),
    meta: {
      class: 'truncate',
    },
    size: 170,
  },
  {
    accessorKey: 'status',
    cell: ({ row }) => {
      const task = row.original;
      const isStale =
        task.status === TaskStatus.OPEN &&
        task.created_at &&
        Date.now() - task.created_at.valueOf() > 24 * 60 * 60 * 1000;

      return h('div', { class: 'flex items-center justify-center gap-1.5 mx-auto' }, [
        h(TaskStatusBadge, { badgeSize: 'xs', task }, () => changeCase.capitalCase(task?.status)),
        isStale &&
          h(Tooltip, [
            h(
              TooltipTrigger,
              { asChild: true },
              h('i', { class: 'fa-solid fa-clock text-amber-500 text-xs' }),
            ),
            h(TooltipContent, { side: 'top' }, 'Open for more than 24 hours'),
          ]),
      ]);
    },
    enableHiding: false,

    enableSorting: false,
    header: ({ column }) =>
      h(DataTableColumnHeader, {
        class: 'flex justify-center text-center',
        column,
        title: 'Status',
      }),
    size: 60,
  },
  {
    accessorKey: 'assignee',
    cell: ({ row }) =>
      h(
        'div',
        { class: 'flex items-center justify-center mx-auto' },
        h(TasksAssigneeSelect, {
          class: 'px-0!',
          ghostButtonVariant: true,
          hideArrowButton: true,
          showAvatarOnly: true,
          task: getTaskStore().getFromTableById(row.original.id),
        }),
      ),
    enableHiding: false,

    enableSorting: false,
    header: ({ column }) =>
      h(DataTableColumnHeader, {
        class: 'text-center',
        column,
        title: 'Assignee',
      }),
    size: 60,
  },
  {
    accessorKey: 'priority',
    cell: ({ row }) => {
      return h(
        'div',
        { class: 'flex items-center justify-center mx-auto' },
        h(
          TaskPriority,
          {
            size: 'xs',
            task: row.original,
            urgentAlwaysBadge: true,
            variant: 'indicator',
          },
          () => row.original?.priority,
        ),
      );
    },
    enableHiding: false,

    enableSorting: true,
    header: ({ column }) =>
      h(DataTableColumnHeader, {
        class: 'text-center',
        column,
        title: 'Priority',
      }),
    size: 60,
    sortDescFirst: true,
  },
  {
    accessorKey: 'created_at',
    cell: ({ row }) =>
      h(
        'div',
        { class: 'flex items-center justify-center mx-auto text-2xs' },
        h(Timestamp, {
          format: 'M/D/YYYY',
          showFromNow: true,
          time: row.original.created_at,
          timezone: null,
        }),
      ),
    enableHiding: false,
    enableSorting: false,

    header: ({ column }) =>
      h(DataTableColumnHeader, { class: 'flex justify-center text-center', column, title: 'Date' }),
    meta: {
      class: 'border-l',
    },
    size: 80,
  },
];

export const TaskTableDrawerColumns: ColumnDef<Task>[] = [
  {
    cell: ({ row }) => {
      return h(
        'div',
        { class: 'flex flex-row justify-center' },
        h(Tooltip, [
          h(
            TooltipTrigger,
            h(Checkbox, {
              ariaLabel: 'Select row',
              disabled: row.original.status !== TaskStatus.OPEN,
              modelValue: row.getIsSelected(),
              'onUpdate:modelValue': (value: any) => row.toggleSelected(Boolean(value)),
            }),
          ),
          row.original.status !== TaskStatus.OPEN &&
            h(
              TooltipContent,
              { class: 'max-w-[250px]', side: 'right' },
              'Only tasks in an Open status can be selected to perform actions on.',
            ),
        ]),
      );
    },
    enableHiding: false,
    enableSorting: false,
    header: ({ table }) => {
      const selectableRows = table
        .getFilteredRowModel()
        .rows.filter((row) => row.original.status === TaskStatus.OPEN);
      const allSelectableSelected =
        selectableRows.length > 0 && selectableRows.every((row) => row.getIsSelected());
      return h(
        'div',
        { class: 'flex flex-row justify-center' },
        h(Checkbox, {
          ariaLabel: 'Select all',
          indeterminate: table.getIsSomePageRowsSelected(),
          modelValue: allSelectableSelected,
          'onUpdate:modelValue': (value: any) =>
            selectableRows.forEach((row) => row.toggleSelected(Boolean(value))),
        }),
      );
    },
    id: 'select',
    size: 36,
  },
  {
    accessorKey: 'partner.computedName',
    cell: ({ row }) =>
      row.original?.partner?.id
        ? h('div', { class: 'flex items-center gap-x-2 truncate' }, [
            h(
              'strong',
              { class: 'truncate font-semibold' },
              row.original?.partner?.computedName as string,
            ),
            h(
              PartnerQuickView,
              { partnerId: row.original?.partner?.id },
              h(
                RouterLink,
                {
                  onClick: (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  },
                  to: {
                    name: 'customers.customer.tasks',
                    params: { id: row.original?.partner?.id },
                  },
                },
                h('i', {
                  class: 'fa-solid fa-up-right-from-square text-blue hover:text-blue-800 text-4xs!',
                }),
              ),
            ),
          ])
        : '--',
    enableHiding: false,
    enableSorting: false,
    header: ({ column }) => h(DataTableColumnHeader, { column, title: 'Customer' }),
    size: 130,
  },
  {
    accessorKey: 'task_type',
    cell: ({ row }) => {
      return h('div', { class: 'flex items-center gap-x-1.5 text-xs' }, [
        h('i', {
          class: ['text-center w-6', row.original.taskTypeIcon, row.original.taskTypeIconClass],
        }),
        h('span', row.original?.taskType),
      ]);
    },
    enableHiding: false,
    enableSorting: false,
    header: ({ column }) => h(DataTableColumnHeader, { column, title: 'Type' }),
    meta: {
      class: 'truncate',
    },
    size: 120,
  },
  {
    accessorKey: 'classifications',
    cell: ({ row }) => {
      const customDescription = h(
        'div',
        { class: 'flex items-center gap-x-1.5 text-xs truncate' },
        [
          h('i', { class: row.original.customDescription?.icon }),
          h('span', { class: 'truncate' }, row.original.customDescription?.label),
        ],
      );

      return row.original?.customDescription?.label
        ? customDescription
        : row.original?.classifications?.length
          ? h(Tooltip, [
              h(
                TooltipTrigger,
                { asChild: true },
                h(
                  'span',
                  { class: 'truncate text-2xs' },
                  row.original?.classifications?.map((c) => humanize(c))?.join(', '),
                ),
              ),
              h(
                TooltipContent,
                { side: 'left' },
                row.original?.classifications?.map((c) => humanize(c))?.join(', '),
              ),
            ])
          : '--';
    },
    enableHiding: false,
    enableSorting: false,
    header: ({ column }) => h(DataTableColumnHeader, { column, title: '' }),
    meta: {
      class: 'truncate',
    },
    size: 150,
  },
  {
    accessorKey: 'assignee',
    cell: ({ row }) =>
      h(
        'div',
        { class: 'flex items-center justify-center mx-auto' },
        h(TasksAssigneeSelect, {
          class: 'px-0!',
          ghostButtonVariant: true,
          hideArrowButton: true,
          showAvatarOnly: true,
          task: getTaskCollectionStore().getById(row.original.id)!,
        }),
      ),
    enableHiding: false,
    enableSorting: false,

    header: ({ column }) =>
      h(DataTableColumnHeader, {
        class: 'text-center',
        column,
        title: 'Assignee',
      }),
    id: 'task_assignee',
    size: 60,
  },
  {
    accessorKey: 'created_at',
    cell: ({ row }) =>
      h(
        'div',
        { class: 'flex items-center justify-center mx-auto text-2xs' },
        h(Timestamp, {
          format: 'M/D/YYYY',
          showFromNow: true,
          time: row.original.created_at,
          timezone: null,
        }),
      ),
    enableHiding: false,

    enableSorting: false,
    header: ({ column }) =>
      h(DataTableColumnHeader, { class: 'text-center', column, title: 'Date' }),
    size: 80,
  },
];

export const TaskGroupedByPartnerColumns: ColumnDef<TaskCollection>[] = [
  {
    accessorKey: 'name',
    cell: ({ row }) =>
      h('div', { class: 'flex items-center gap-x-2.5 pl-1' }, [
        h('i', { class: 'far fa-diagram-subtask text-primary' }),
        h('div', { class: 'flex items-center gap-x-2 truncate' }, [
          h('strong', { class: 'truncate font-semibold' }, row.original?.name),
          h(
            PartnerQuickView,
            { partnerId: row.original?.id },
            h(
              RouterLink,
              {
                onClick: (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                },
                to: {
                  name: 'customers.customer',
                  params: { id: row.original?.id },
                },
              },
              h('i', {
                class: 'fa-solid fa-up-right-from-square text-blue hover:text-blue-800 text-4xs!',
              }),
            ),
          ),
        ]),
      ]),
    enableHiding: false,
    enableSorting: false,
    header: ({ column }) => h(DataTableColumnHeader, { column }),
    size: 130,
  },
  {
    accessorKey: 'task_count',
    cell: ({ row }) =>
      h(
        'div',
        { class: 'flex justify-end pr-3' },
        h(
          'div',
          {
            class:
              'flex items-center justify-center bg-accent group-hover:bg-accent rounded size-6 text-center font-semibold text-2xs transition-colors duration-300',
          },
          row.original?.task_count || 0,
        ),
      ),
    enableHiding: false,

    enableSorting: false,
    header: ({ column }) =>
      h(DataTableColumnHeader, {
        column,
      }),
    size: 80,
  },
];

export const TaskGroupedByTypeColumns: ColumnDef<TaskCollection>[] = [
  {
    accessorKey: 'task_type',
    cell: ({ row }) =>
      h('div', { class: 'flex items-center gap-x-2.5' }, [
        h('i', { class: 'far fa-diagram-subtask text-primary' }),
        h(
          'span',
          { class: 'font-semibold' },
          TaskTypeTransformMap[row.original?.id] || humanize(row.original?.name),
        ),
      ]),
    enableHiding: false,
    enableSorting: false,
    header: ({ column }) => h(DataTableColumnHeader, { column, title: 'Type' }),
    meta: {
      class: 'truncate',
    },
    size: 130,
  },
  {
    accessorKey: 'task_count',
    cell: ({ row }) =>
      h(
        'div',
        { class: 'flex justify-end pr-3' },
        h(
          'div',
          {
            class:
              'flex items-center justify-center bg-accent group-hover:bg-accent rounded size-6 text-center font-semibold text-2xs transition-colors duration-300',
          },
          row.original?.task_count || 0,
        ),
      ),
    enableHiding: false,

    enableSorting: false,
    header: ({ column }) =>
      h(DataTableColumnHeader, {
        column,
      }),
    size: 80,
  },
];

export default TaskTableColumns;
